import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { SwUpdate, UnrecoverableStateEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

@Injectable({
    providedIn: 'root',
})
export class PwaService implements OnDestroy {
    private intervalSubscription: Subscription | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private swUpdate: SwUpdate,
        private snackBar: MatSnackBar,
        private zone: NgZone
    ) {
        this.initializeUpdateChecking();
    }

    /**
     * True when the app shell currently on screen is the anonymous login page.
     * On that route there is no user work to lose, so it is safe to reload
     * immediately once a new build is ready — this is what fixes the
     * "first load shows the old login without the Cognito button, a manual
     * reload brings it back" symptom (HIP-36): the stale app shell is swapped
     * for the fresh one without the user having to reload by hand.
     */
    private isOnLoginPage(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.location.pathname.startsWith('/sign-in');
    }

    private initializeUpdateChecking(): void {
        if (!this.swUpdate.isEnabled) {
            console.log('[PWA] Service Worker is not enabled');
            return;
        }

        console.log(
            '[PWA] Service Worker is enabled, initializing update checking'
        );

        // Check for updates on service worker initialization
        this.checkForUpdate();

        // Subscribe to all version update events for debugging
        this.swUpdate.versionUpdates
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[PWA] Version update event:', event.type, event);

                if (event.type === 'VERSION_READY') {
                    // Nicht sofort aktivieren: nach activateUpdate() liefert
                    // der Service Worker die Chunks des alten Builds nicht
                    // mehr aus, und jede noch nicht geladene Lazy-Route bleibt
                    // dann in einer endlosen Ladeschleife hängen (HIP-79).
                    // Aktiviert wird erst unmittelbar vor dem Reload.
                    if (this.isOnLoginPage()) {
                        this.activateUpdate();
                    } else {
                        this.promptReloadForUpdate();
                    }
                } else if (event.type === 'VERSION_DETECTED') {
                    console.log('[PWA] New version detected, downloading...');
                } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
                    console.error('[PWA] Version installation failed:', event);
                    // Report to Sentry: a failing installation pins every user
                    // to the build they already have cached, and console output
                    // is stubbed out in production, so this would otherwise be
                    // completely invisible (HIP-37).
                    Sentry.captureMessage(
                        `[PWA] Service Worker version installation failed: ${event.error}`,
                        'error'
                    );
                }
            });

        // Handle unrecoverable state - requires page reload
        this.swUpdate.unrecoverable
            .pipe(takeUntil(this.destroy$))
            .subscribe((event: UnrecoverableStateEvent) => {
                console.error('[PWA] Unrecoverable state:', event.reason);
                this.promptUnrecoverableState(event);
            });

        // Set up periodic update checks
        this.setupPeriodicUpdateChecks();
    }

    private checkForUpdate(): void {
        console.log('[PWA] Checking for updates...');
        this.swUpdate
            .checkForUpdate()
            .then(updateFound => {
                console.log(
                    '[PWA] Update check completed. Update found:',
                    updateFound
                );
            })
            .catch(err => {
                console.error('[PWA] Error checking for updates:', err);
            });
    }

    private setupPeriodicUpdateChecks(): void {
        this.zone.runOutsideAngular(() => {
            // Check every 5 minutes (reduced from 15 for faster detection)
            this.intervalSubscription = interval(5 * 60 * 1000)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.zone.run(() => this.checkForUpdate());
                });
        });
    }

    /**
     * On the login page there is no user work to lose, so activate and reload
     * right away — the visitor lands on the fresh build instead of a stale
     * cached shell that they'd otherwise have to reload by hand (HIP-36).
     */
    private activateUpdate(): void {
        console.log('[PWA] Activating update on login page...');
        this.swUpdate
            .activateUpdate()
            .then(() => {
                window.location.reload();
            })
            .catch(err => {
                console.error('[PWA] Error activating update:', err);
            });
    }

    /**
     * Mid-session, tell the user an update is waiting instead of activating
     * it silently. The silent variant left people on the old bundle with no
     * hint that anything had changed, so they never reloaded (HIP-37). The
     * snackbar does not auto-dismiss and reloading stays the user's choice,
     * so no in-progress work is thrown away. The update is only activated
     * right before the reload (HIP-79).
     */
    private promptReloadForUpdate(): void {
        const snackBarRef = this.snackBar.open(
            'Eine neue Version ist verfügbar.',
            'Neu laden',
            {
                duration: 0,
                panelClass: ['update-snackbar'],
            }
        );

        snackBarRef.onAction().subscribe(() => {
            this.swUpdate
                .activateUpdate()
                .catch(() => {})
                .then(() => {
                    window.location.reload();
                });
        });
    }

    private promptUnrecoverableState(event: UnrecoverableStateEvent): void {
        console.error(
            '[PWA] Service worker in unrecoverable state:',
            event.reason
        );

        const snackBarRef = this.snackBar.open(
            'Die App muss neu geladen werden.',
            'Neu laden',
            {
                duration: 0,
                panelClass: ['update-snackbar'],
            }
        );

        snackBarRef.onAction().subscribe(() => {
            window.location.reload();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.intervalSubscription) {
            this.intervalSubscription.unsubscribe();
        }
    }
}
